/**
 * Analysis adjustments — the vocabulary every act that changes a reading is
 * recorded in: whose side the problem was on, what exactly went wrong, and a
 * note for our own records.
 *
 * One source of truth because the same lists are offered as chips in the
 * teacher app and admin console, validated by every endpoint that takes them,
 * and grouped on by the reports. A chip the server does not know turns a
 * correction into a 400.
 *
 * See docs/FeatureDesigns/AssessmentAndMarking/analysis-adjustments-target.md §4.
 */

/** Whose side the problem was on. Reports split by this first: a student-side
 *  problem is a habit to teach, a system-side one is a bug to fix. */
export const ADJUSTMENT_ORIGINS = ['student', 'upload', 'system', 'content', 'policy'] as const;
export type AdjustmentOrigin = typeof ADJUSTMENT_ORIGINS[number];

/** What exactly went wrong, within each origin. */
export const ADJUSTMENT_REASONS_BY_ORIGIN = {
    student: ['no_question_number', 'work_out_of_order', 'answer_not_marked', 'illegible', 'student_other'],
    upload: ['page_missing', 'page_quality', 'wrong_file', 'duplicate_upload'],
    system: ['wrong_region', 'misread', 'wrong_marking', 'wrong_diagnosis', 'system_other'],
    content: ['wrong_key', 'wrong_question', 'wrong_marks'],
    policy: ['policy_change'],
} as const satisfies Record<AdjustmentOrigin, readonly string[]>;

export type AdjustmentReason = typeof ADJUSTMENT_REASONS_BY_ORIGIN[AdjustmentOrigin][number];

export const ADJUSTMENT_REASONS: readonly AdjustmentReason[] =
    Object.values(ADJUSTMENT_REASONS_BY_ORIGIN).flat();

/** Reasons that say nothing on their own, so the internal comment is required. */
export const ADJUSTMENT_REASONS_NEEDING_COMMENT: readonly AdjustmentReason[] = ['student_other', 'system_other'];

/** Labels for the chips. Origins name the side, not a culprit. */
export const ADJUSTMENT_ORIGIN_LABELS: Record<AdjustmentOrigin, string> = {
    student: 'Student',
    upload: 'Upload / scan',
    system: 'Our reading',
    content: 'Question / key',
    policy: 'Rule change',
};

export const ADJUSTMENT_REASON_LABELS: Record<AdjustmentReason, string> = {
    no_question_number: 'No question number',
    work_out_of_order: 'Work out of order',
    answer_not_marked: 'Answer not marked',
    illegible: 'Illegible',
    student_other: 'Other',
    page_missing: 'Page missing',
    page_quality: 'Poor scan',
    wrong_file: 'Wrong file',
    duplicate_upload: 'Duplicate upload',
    wrong_region: 'Wrong region',
    misread: 'Misread',
    wrong_marking: 'Wrong marking',
    wrong_diagnosis: 'Wrong diagnosis',
    system_other: 'Other',
    wrong_key: 'Wrong key',
    wrong_question: 'Wrong question',
    wrong_marks: 'Wrong marks',
    policy_change: 'Rule change',
};

/** Long enough to explain a pattern ("splitter merged Q10/Q11 on every RG scan
 *  this week"), short enough that nobody pastes a page into it. */
export const ADJUSTMENT_COMMENT_MAX_LENGTH = 1000;

export function isReasonOfOrigin(origin: AdjustmentOrigin, reason: AdjustmentReason): boolean {
    return (ADJUSTMENT_REASONS_BY_ORIGIN[origin] as readonly string[]).includes(reason);
}

/** Each reason belongs to exactly one side, so a reason names its origin. */
export function originOfReason(reason: AdjustmentReason): AdjustmentOrigin {
    return ADJUSTMENT_ORIGINS.find(o => isReasonOfOrigin(o, reason))!;
}

/**
 * One adjustment as the teacher's History panel reads it: what the score
 * history endpoint returns beside the versions. A projection of the
 * `analysis-adjustments` row — the actor's name resolved, the internal note
 * only where the reader may see it.
 *
 * See docs/FeatureDesigns/solution-analysis/teacher-attempt-view-target.md §History.
 */
/**
 * One reading of a question, cut to what History's before/after view compares:
 * the scores, the verdict, the diagnosis, the steps and the advice. Built by the
 * score history endpoint for every reading History names — each version's, and
 * both sides of a re-read — with the teacher's withholding already applied, so
 * it never shows a diagnosis the attempt page would not.
 *
 * See docs/FeatureDesigns/solution-analysis/teacher-attempt-view-target.md §History.
 */
export interface ReadingSnapshot {
    submissionId: string;
    /** When this reading was made. */
    at?: string;
    finalScore?: number;
    correctnessScore?: number;
    approachScore?: number;
    verdict?: string;
    diagnosis?: { kind?: string; event?: string; cause?: string; stepRef?: string };
    steps: Array<{
        stepNumber: number;
        content: string;
        feedback?: string;
        isCorrect: boolean;
        locallyValid?: 'yes' | 'no' | 'unsure';
        onCorrectPath?: 'yes' | 'no' | 'unsure';
        crossedOut?: boolean;
    }>;
    /** The next step(s) the analysis gave. */
    advice: string[];
}

export interface AdjustmentHistoryRow {
    id: string;
    /** `AdjustmentKind` on the backend row. */
    kind: string;
    at: string;
    actor: { role: string; name?: string };
    origin?: AdjustmentOrigin;
    reasonCode?: AdjustmentReason;
    /** The internal comment — absent when the reader may not see it. */
    note?: string;
    /** What was sent to the model with the re-mark. */
    modelNote?: string;
    /** A thumbs-down's reason, or a moment rejection's. */
    comment?: string;
    before?: { finalScore?: number; correctnessScore?: number; marksAwarded?: number };
    after?: { finalScore?: number; correctnessScore?: number; marksAwarded?: number };
    outcome: 'applied' | 'failed' | 'no_change';
    fromSubmissionId?: string;
    toSubmissionId?: string;
    /** `moment_reject`: whether it was a rejection or its lifting. */
    rejected?: boolean;
    learningMomentId?: string;
    /** `content_cleanup`: the script that made it. */
    scriptName?: string;
    /** `step_mark`: the step's position and the mark (`clear` — taken off). */
    step?: number;
    stepVerdict?: 'right' | 'wrong' | 'clear';
}
